import type { JSX, MouseEvent } from "react";
import { Icon } from "./icons";
import type { EditorLabels } from "./labels";

// A button that took focus would close an on-screen keyboard and lose the caret.
const keepSelection = (event: MouseEvent): void => event.preventDefault();

export interface SendButtonProps {
  labels: EditorLabels;
  submit: () => void;
}

export interface SendControlProps extends SendButtonProps {
  /** A send is waiting for the host. */
  sending: boolean;
  /** The empty-message policy says there is nothing to send. */
  empty: boolean;
}

/**
 * The composer's send control, for keyboards with no Shift+Enter. It sits outside the editable, is
 * the next Tab stop after it, and goes through the same submit path as Enter. It is told how the
 * draft stands rather than reading it from the composer's store, so it stands alone.
 */
export function SendControl({ labels, submit, sending, empty }: SendControlProps): JSX.Element {
  return (
    <button
      type="button"
      className="ze-send"
      aria-label={sending ? labels.sending : labels.send}
      // Not `disabled`: that would drop keyboard focus from the button in the middle of a send, and
      // from under someone who has just deleted the last character.
      aria-disabled={sending || empty ? true : undefined}
      data-pending={sending ? "" : undefined}
      data-empty={empty ? "" : undefined}
      onMouseDown={keepSelection}
      onClick={submit}
    >
      <Icon name="send" />
    </button>
  );
}
